import * as React from "react"
import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { t, type Language } from "../lib/i18n"
import { sanitizeHtml } from "../lib/sanitize"
import { useLanguage } from "@/lib/lang"
import { prefetchScheme, getCachedScheme } from "@/lib/schemes"

function setMetaTag(name: string, content: string) {
  const existing = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (existing) {
    existing.content = content
    return
  }
  const meta = document.createElement("meta")
  meta.setAttribute("name", name)
  meta.setAttribute("content", content)
  document.head.appendChild(meta)
}

function setLinkTag(rel: string, href: string) {
  const existing = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (existing) {
    existing.href = href
    return
  }
  const link = document.createElement("link")
  link.setAttribute("rel", rel)
  link.setAttribute("href", href)
  document.head.appendChild(link)
}

// Section component no longer used in MGNREGSPage after HTML-based translation

const MGNREGSPage: React.FC = () => {
  const { lang, isSwitching } = useLanguage()
  const [html, setHtml] = useState<string>("")
  // Language sync and persistence handled by provider

  useEffect(() => {
    const prefix = lang === "hi" ? t("scheme_prefix", "hi") : lang === "pa" ? t("scheme_prefix", "pa") : t("scheme_prefix", "en")
    const seoTitle = `${prefix}: MGNREGS`
    const dept = lang === "hi" ? "ग्रामीण विकास विभाग | हरियाणा" : lang === "pa" ? "ਪਿੰਡ ਵਿਕਾਸ ਵਿਭਾਗ | ਹਰਿਆਣਾ" : "Rural Development Department | Haryana"
    document.title = `${seoTitle} | ${dept}`
    const desc = lang === "hi" ? t("scheme_meta_description", "hi") : lang === "pa" ? t("scheme_meta_description", "pa") : t("scheme_meta_description", "en")
    setMetaTag("description", desc)
    setMetaTag("og:title", seoTitle)
    setMetaTag("og:type", "article")
    setLinkTag("canonical", "https://haryanarural.gov.in/mahatma-gandhi-national-rural-employment-guarantee-scheme-mgnregs/")

    const contentHtmlEn = `
      <section>
        <h2>Preliminary</h2>
        <p>
          The Government of India passed the Mahatma Gandhi National Rural Employment Guarantee Act, 2005 in September, 2005. The Act gives legal guarantee of a hundred days of wage employment in a financial year to adult members of a rural household who demand employment and are willing to do unskilled manual work. The Act will be applicable to areas notified by the Central Government. The objective of the Act is to enhance the livelihood security of the people in the rural areas by generating wage employment through works that develop the infrastructure base of that area.
        </p>
      </section>
      <section>
        <h2>Scheme Brief</h2>
        <p>
          Mahatma Gandhi National Rural Employment Guarantee Act, 2005 stipulates that every State Govt. shall notify a scheme and therefore, “Haryana Rural Employment Guarantee Scheme” had been formulated in accordance with the operational guidelines and provisions of the National Rural Employment Guarantee Act, 2005. The said scheme had also been notified in the Haryana Government Gazette (extra), March, 16, 2007 by the State Government.
        </p>
      </section>
      <section>
        <h2>Launching of Scheme</h2>
        <p>
          Mahatma Gandhi National Rural Employment Guarantee Scheme was launched by Govt. of India in all Gram Panchayats of districts Mahendergarh and Sirsa on 2nd February, 2006 and this scheme was also extended in two more districts namely Ambala & Mewat w.e.f. 1st April, 2007. The remaining districts of the State have been covered under the scheme w.e.f. 1.4.2008.
        </p>
      </section>
      <section>
        <h2>Objectives</h2>
        <p>
          Enhancement of livelihood security to the households in rural areas of the State by providing round the year employment with minimum guarantee of one hundred days of wage employment in a financial year to every household volunteer to do unskilled manual work. Secondary objective includes creation of assets for development of rural areas.
        </p>
      </section>
      <section>
        <h2>Cost Sharing</h2>
        <p>
          Financial assistance shall be provided by Central and State Governments in the ratio of 90:10 respectively.
        </p>
      </section>
      <section>
        <h2>Target Group</h2>
        <ul>
          <li>Scheme is open to all rural households who are in need of wage employment and desire to do manual and unskilled work.</li>
          <li>Period of employment should ordinarily be atleast fourteen days continuously with not more than six days in a week.</li>
          <li>Priority to works where atleast one third of wage seekers are women.</li>
          <li>Persons desirous for work may submit their applications to the Gram Panchayat.</li>
          <li>Gram Panchayat shall register the household. After verification, job card is to be issued to the applicant household.</li>
          <li>Gram Panchayat or Block Programme Officer shall provide unskilled manual work to the applicant within fifteen days of receipt of application preferably within a radius of 5 kilometers of the village, where the applicant resides. In case the employment is provided outside such radius, it must be provided within the Block and the laborers shall be paid 10% of the wage rate as extra wages to meet additional transportation and living expenses.</li>
        </ul>
      </section>
      <section>
        <h2>Programme Strategy</h2>
        <p>
          The programme will be implemented through Panchayati Raj Institutions. The Block Programme Officer shall allot fifty-percent works in terms cost to the Gram Panchayat. Gram Sabha shall recommend works and the Gram Panchayat shall identify/approve the works for implementation.
        </p>
        <p>
          The Block Programme Officer will scrutinize the Annual Plan. The Panchayat Samiti will maintain the priority of works indicated by the Gram Panchayat and approve the Block level Plan.
        </p>
        <p>
          The Chief Executive Officer is designated as District Programme Coordinator for implementation of the Act/Scheme in the district. The DPC shall scrutinize the plan proposals of the Panchayat Samitis. The whole exercise shall be completed by DPC before end of December each year.
        </p>
      </section>
      <section>
        <h2>Permissible Works</h2>
        <p>The following works are permissible under MGNREGS:</p>
        <ul>
          <li>Water conservation and water harvesting including contour trenches, contour bunds, boulder checks, gabion structures, underground dykes, earthen dams, stop dams and springshed development;</li>
          <li>Drought proofing including afforestation and tree plantation;</li>
          <li>Irrigation canals, including micro and minor irrigation works;</li>
          <li>Provision of irrigation facility, dug out farm pond, horticulture, plantation, farm bunding and land development on land owned by households specified in paragraph IC;</li>
          <li>Renovation of traditional water bodies including desilting of tanks;</li>
          <li>Land development;</li>
          <li>Flood control and protection works including drainage in water logged areas including deepening and repairing of flood channels, chaur renovation, construction of storm water drains for coastal protection;</li>
          <li>Rural connectivity to provide all weather access, including culverts and roads within a village, wherever necessary;</li>
          <li>Construction of Bharat Nirman Rajiv Gandhi Sewa Kendra as Knowledge Resource Centre at the Block level and as Gram Panchayat Bhawan at the Gram Panchayat level;</li>
          <li>Agriculture related works, such as, NADEP composting, vermicomposting, liquid bio-manures;</li>
          <li>Livestock related works, such as, poultry shelter, goat shelter, construction of pucca floor, urine tank and fodder trough for cattle, azolla as cattle-feed supplement;</li>
          <li>Fisheries related works, such as, fisheries in seasonal water bodies on public land;</li>
        </ul>
      </section>
      <footer class="mt-8 text-[15px] text-gray-500" aria-label="Attribution">Source: Rural Development Department, Haryana.</footer>
    `

    const contentHtmlPa = `
      <section>
        <h2>ਪ੍ਰਾਰੰਭਿਕ</h2>
        <p>
          ਭਾਰਤ ਸਰਕਾਰ ਨੇ ਸਤੰਬਰ 2005 ਵਿੱਚ ਮਹਾਤਮਾ ਗਾਂਧੀ ਰਾਸ਼ਟਰੀ ਪਿੰਡ ਰੋਜ਼ਗਾਰ ਗਾਰੰਟੀ ਕਾਨੂੰਨ, 2005 (MGNREGA) ਪਾਸ ਕੀਤਾ। ਇਹ ਕਾਨੂੰਨ ਉਹਨਾਂ ਪਿੰਡ ਘਰਾਂ ਦੇ ਵੱਡੇ ਮੈਂਬਰਾਂ ਨੂੰ, ਜੋ ਰੋਜ਼ਗਾਰ ਦੀ ਮੰਗ ਕਰਦੇ ਹਨ ਅਤੇ ਬਿਨਾ ਦੱਖਲ ਦੇ ਹੱਥੀਂ ਕੰਮ ਕਰਨ ਲਈ ਤਿਆਰ ਹਨ, ਇੱਕ ਆਰਥਿਕ ਵਰ੍ਹੇ ਵਿੱਚ ਸੌ ਦਿਨਾਂ ਦੇ ਮਜ਼ਦੂਰੀ ਰੋਜ਼ਗਾਰ ਦੀ ਕਾਨੂੰਨੀ ਗਾਰੰਟੀ ਦਿੰਦਾ ਹੈ। ਇਹ ਕਾਨੂੰਨ ਉਹਨਾਂ ਖੇਤਰਾਂ ਵਿੱਚ ਲਾਗੂ ਹੈ ਜਿਨ੍ਹਾਂ ਨੂੰ ਕੇਂਦਰ ਸਰਕਾਰ ਦੁਆਰਾ ਅਧਿਸੂਚਿਤ ਕੀਤਾ ਗਿਆ ਹੈ। ਇਸ ਕਾਨੂੰਨ ਦਾ ਉਦੇਸ਼ ਪਿੰਡ ਖੇਤਰਾਂ ਦੇ ਲੋਕਾਂ ਦੀ ਜੀਵਿਕਾ ਸੁਰੱਖਿਆ ਨੂੰ ਮਜ਼ਬੂਤ ਕਰਨਾ ਹੈ, ਜਿਸ ਲਈ ਉਸ ਖੇਤਰ ਦੇ ਬੁਨਿਆਦੀ ਢਾਂਚੇ ਨੂੰ ਵਿਕਸਤ ਕਰਨ ਵਾਲੇ ਕੰਮਾਂ ਰਾਹੀਂ ਮਜ਼ਦੂਰੀ ਰੋਜ਼ਗਾਰ ਪੈਦਾ ਕੀਤਾ ਜਾਂਦਾ ਹੈ।
        </p>
      </section>
      <section>
        <h2>ਯੋਜਨਾ ਸੰਖੇਪ</h2>
        <p>
          ਮਹਾਤਮਾ ਗਾਂਧੀ ਰਾਸ਼ਟਰੀ ਪਿੰਡ ਰੋਜ਼ਗਾਰ ਗਾਰੰਟੀ ਕਾਨੂੰਨ, 2005 ਅਨੁਸਾਰ ਹਰ ਰਾਜ ਸਰਕਾਰ ਇੱਕ ਯੋਜਨਾ ਅਧਿਸੂਚਿਤ ਕਰੇਗੀ। ਇਸ ਸੰਦਰਭ ਵਿੱਚ “ਹਰਿਆਣਾ ਰੂਰਲ ਇੰਪਲਾਇਮੈਂਟ ਗਾਰੰਟੀ ਸਕੀਮ” ਨੂੰ ਰਾਸ਼ਟਰੀ ਰੂਰਲ ਇੰਪਲਾਇਮੈਂਟ ਗਾਰੰਟੀ ਐਕਟ, 2005 ਦੇ ਕਾਰਜ-ਨਿਰਦੇਸ਼ਾਂ ਅਤੇ ਪ੍ਰਾਵਧਾਨਾਂ ਦੇ ਅਨੁਸਾਰ ਤਿਆਰ ਕੀਤਾ ਗਿਆ। ਇਸ ਯੋਜਨਾ ਨੂੰ ਰਾਜ ਸਰਕਾਰ ਦੁਆਰਾ 16 ਮਾਰਚ, 2007 ਨੂੰ ਹਰਿਆਣਾ ਗੈਜ਼ੇਟ (ਵਿਖੇ) ਵਿੱਚ ਅਧਿਸੂਚਿਤ ਕੀਤਾ ਗਿਆ।
        </p>
      </section>
      <section>
        <h2>ਯੋਜਨਾ ਦੀ ਸ਼ੁਰੂਆਤ</h2>
        <p>
          ਮਹਾਤਮਾ ਗਾਂਧੀ ਰਾਸ਼ਟਰੀ ਪਿੰਡ ਰੋਜ਼ਗਾਰ ਗਾਰੰਟੀ ਯੋਜਨਾ (MGNREGS) ਨੂੰ ਭਾਰਤ ਸਰਕਾਰ ਨੇ 2 ਫ਼ਰਵਰੀ, 2006 ਨੂੰ ਜ਼ਿਲ੍ਹਾ ਮਹਿੰਦਰਗੜ੍ਹ ਅਤੇ ਸਿਰਸਾ ਦੀਆਂ ਸਾਰੀਆਂ ਗ੍ਰਾਮ ਪੰਚਾਇਤਾਂ ਵਿੱਚ ਸ਼ੁਰੂ ਕੀਤਾ, ਅਤੇ 1 ਅਪਰੈਲ, 2007 ਤੋਂ ਇਸਨੂੰ ਹੋਰ ਦੋ ਜ਼ਿਲ੍ਹਿਆਂ—ਅੰਬਾਲਾ ਅਤੇ ਮੇਵਾਤ—ਵਿੱਚ ਵਧਾਇਆ ਗਿਆ। ਰਾਜ ਦੇ ਬਾਕੀ ਜ਼ਿਲ੍ਹਿਆਂ ਨੂੰ 1.4.2008 ਤੋਂ ਯੋਜਨਾ ਦੇ ਤਹਿਤ ਸ਼ਾਮਲ ਕੀਤਾ ਗਿਆ ਹੈ।
        </p>
      </section>
      <section>
        <h2>ਉਦੇਸ਼</h2>
        <p>
          ਰਾਜ ਦੇ ਪਿੰਡ ਖੇਤਰਾਂ ਦੇ ਪਰਿਵਾਰਾਂ ਦੀ ਜੀਵਿਕਾ ਸੁਰੱਖਿਆ ਵਧਾਉਣਾ, ਸਾਲ ਭਰ ਰੋਜ਼ਗਾਰ ਪ੍ਰਦਾਨ ਕਰਨਾ ਅਤੇ ਹਰ ਪਰਿਵਾਰ ਨੂੰ ਇੱਕ ਆਰਥਿਕ ਵਰ੍ਹੇ ਵਿੱਚ ਘੱਟੋ-ਘੱਟ ਸੌ ਦਿਨਾਂ ਦਾ ਮਜ਼ਦੂਰੀ ਰੋਜ਼ਗਾਰ ਉਪਲਬਧ ਕਰਵਾਉਣਾ—ਸ਼ਰਤ ਇਹ ਕਿ ਉਹ ਬਿਨਾ ਦੱਖਲ ਦੇ ਹੱਥੀਂ ਕੰਮ ਕਰਨ ਲਈ ਤਿਆਰ ਹੋਣ। ਦੂਜਾ ਉਦੇਸ਼ ਪਿੰਡ ਖੇਤਰਾਂ ਦੇ ਵਿਕਾਸ ਲਈ ਸੰਪਤੀਆਂ ਦਾ ਸਿਰਜਣ ਕਰਨਾ ਹੈ।
        </p>
      </section>
      <section>
        <h2>ਵਿੱਤੀ ਭਾਗੀਦਾਰੀ</h2>
        <p>
          ਵਿੱਤੀ ਸਹਾਇਤਾ ਕੇਂਦਰ ਅਤੇ ਰਾਜ ਸਰਕਾਰਾਂ ਵੱਲੋਂ 90:10 ਦੇ ਅਨੁਪਾਤ ਵਿੱਚ ਪ੍ਰਦਾਨ ਕੀਤੀ ਜਾਵੇਗੀ।
        </p>
      </section>
      <section>
        <h2>ਲਕਸ਼ਿਤ ਸਮੂਹ</h2>
        <ul>
          <li>ਯੋਜਨਾ ਉਹਨਾਂ ਸਾਰੇ ਪਿੰਡ ਘਰਾਂ ਲਈ ਖੁੱਲੀ ਹੈ ਜਿਹਨਾਂ ਨੂੰ ਮਜ਼ਦੂਰੀ ਰੋਜ਼ਗਾਰ ਦੀ ਲੋੜ ਹੈ ਅਤੇ ਬਿਨਾ ਦੱਖਲ ਹੱਥੀਂ ਕੰਮ ਕਰਨ ਦੀ ਇੱਛਾ ਹੈ।</li>
          <li>ਰੋਜ਼ਗਾਰ ਦੀ ਮਿਆਦ ਆਮ ਤੌਰ 'ਤੇ ਘੱਟੋ-ਘੱਟ ਚੌਦਾਂ ਲਗਾਤਾਰ ਦਿਨ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ, ਹਫ਼ਤੇ ਵਿੱਚ ਛੇ ਦਿਨਾਂ ਤੋਂ ਵੱਧ ਨਹੀਂ।</li>
          <li>ਉਨ੍ਹਾਂ ਕੰਮਾਂ ਨੂੰ ਤਰਜੀਹ ਜਿੱਥੇ ਘੱਟੋ-ਘੱਟ ਇੱਕ-ਤਿਹਾਈ ਮਜ਼ਦੂਰੀ ਮੰਗਣ ਵਾਲੀਆਂ ਔਰਤਾਂ ਹੋਣ।</li>
          <li>ਕੰਮ ਦੇ ਇੱਛੁਕ ਵਿਅਕਤੀ ਆਪਣੀਆਂ ਅਰਜ਼ੀਆਂ ਗ੍ਰਾਮ ਪੰਚਾਇਤ ਵਿੱਚ ਪੇਸ਼ ਕਰ ਸਕਦੇ ਹਨ।</li>
          <li>ਗ੍ਰਾਮ ਪੰਚਾਇਤ ਪਰਿਵਾਰ ਦਾ ਰਜਿਸਟ੍ਰੇਸ਼ਨ ਕਰੇਗੀ। ਤਸਦੀਕ ਤੋਂ ਬਾਅਦ, ਅਰਜ਼ੀਕਰਤਾ ਪਰਿਵਾਰ ਨੂੰ ਜਾਬ ਕਾਰਡ ਜਾਰੀ ਕੀਤਾ ਜਾਵੇਗਾ।</li>
          <li>ਗ੍ਰਾਮ ਪੰਚਾਇਤ ਜਾਂ ਬਲਾਕ ਪ੍ਰੋਗਰਾਮ ਅਧਿਕਾਰੀ ਅਰਜ਼ੀ ਪ੍ਰਾਪਤ ਹੋਣ ਤੋਂ ਪੰਦਰਾਂ ਦਿਨਾਂ ਦੇ ਅੰਦਰ ਅਰਜ਼ੀਕਰਤਾ ਨੂੰ ਬਿਨਾ ਦੱਖਲ ਹੱਥੀਂ ਕੰਮ ਪ੍ਰਦਾਨ ਕਰਨਗੇ; ਵਧੀਆ ਤੌਰ 'ਤੇ ਪਿੰਡ ਤੋਂ 5 ਕਿ.ਮੀ. ਦੇ ਘੇਰੇ ਵਿੱਚ। ਜੇ ਰੋਜ਼ਗਾਰ ਇਸ ਘੇਰੇ ਤੋਂ ਬਾਹਰ ਦਿੱਤਾ ਜਾਂਦਾ ਹੈ, ਤਾਂ ਇਹ ਉਸੇ ਬਲਾਕ ਦੇ ਅੰਦਰ ਹੋਵੇਗਾ ਅਤੇ ਮਜ਼ਦੂਰਾਂ ਨੂੰ ਵਾਧੂ ਆਵਾਜਾਈ ਅਤੇ ਰਹਾਇਸ਼ ਖ਼ਰਚਿਆਂ ਲਈ ਮਜ਼ਦੂਰੀ ਦਰ ਦਾ 10% ਵਾਧੂ ਭੁਗਤਾਨ ਕੀਤਾ ਜਾਵੇਗਾ।</li>
        </ul>
      </section>
      <section>
        <h2>ਕਾਰਜਕ੍ਰਮ ਰਣਨੀਤੀ</h2>
        <p>
          ਕਾਰਜਕ੍ਰਮ ਦਾ ਕਾਰਜਨਵਿਅਨ ਪੰਚਾਇਤੀ ਰਾਜ ਸੰਸਥਾਵਾਂ ਰਾਹੀਂ ਕੀਤਾ ਜਾਵੇਗਾ। ਬਲਾਕ ਪ੍ਰੋਗਰਾਮ ਅਧਿਕਾਰੀ ਲਾਗਤ ਦੇ ਅਧਾਰ 'ਤੇ ਪੰਜਾਹ ਪ੍ਰਤੀਸ਼ਤ ਕੰਮ ਗ੍ਰਾਮ ਪੰਚਾਇਤ ਨੂੰ ਆਵੰਟਿਤ ਕਰਨਗੇ। ਗ੍ਰਾਮ ਸਭਾ ਕੰਮਾਂ ਦੀ ਸਿਫ਼ਾਰਸ਼ ਕਰੇਗੀ ਅਤੇ ਗ੍ਰਾਮ ਪੰਚਾਇਤ ਕਾਰਜਨਵਿਅਨ ਲਈ ਕੰਮਾਂ ਦੀ ਪਛਾਣ/ਮਨਜ਼ੂਰੀ ਕਰੇਗੀ।
        </p>
        <p>
          ਬਲਾਕ ਪ੍ਰੋਗਰਾਮ ਅਧਿਕਾਰੀ ਸਲਾਨਾ ਯੋਜਨਾ ਦੀ ਜਾਂਚ ਕਰਨਗੇ। ਪੰਚਾਇਤ ਸਮਿਤੀ ਗ੍ਰਾਮ ਪੰਚਾਇਤ ਦੁਆਰਾ ਦਰਸਾਈ ਤਰਜੀਹ ਨੂੰ ਕਾਇਮ ਰੱਖੇਗੀ ਅਤੇ ਬਲਾਕ-ਪੱਧਰ ਯੋਜਨਾ ਨੂੰ ਮਨਜ਼ੂਰ ਕਰੇਗੀ।
        </p>
        <p>
          ਮੁੱਖ ਕਾਰਜਕਾਰੀ ਅਧਿਕਾਰੀ ਨੂੰ ਜ਼ਿਲ੍ਹੇ ਵਿੱਚ ਐਕਟ/ਯੋਜਨਾ ਦੇ ਕਾਰਜਨਵਿਅਨ ਲਈ ਜ਼ਿਲ੍ਹਾ ਪ੍ਰੋਗਰਾਮ ਕੋਆਰਡੀਨੇਟਰ (DPC) ਨਾਮਜ਼ਦ ਕੀਤਾ ਗਿਆ ਹੈ। DPC ਪੰਚਾਇਤ ਸਮਿਤੀਆਂ ਦੇ ਯੋਜਨਾ ਪ੍ਰਸਤਾਵਾਂ ਦੀ ਜਾਂਚ ਕਰੇਗਾ। ਪੂਰੀ ਪ੍ਰਕਿਰਿਆ ਹਰ ਸਾਲ ਦਸੰਬਰ ਦੇ ਅੰਤ ਤੱਕ DPC ਦੁਆਰਾ ਪੂਰੀ ਕੀਤੀ ਜਾਵੇਗੀ।
        </p>
      </section>
      <section>
        <h2>ਅਨੁਮਤ ਕੰਮ</h2>
        <p>MGNREGS ਦੇ ਤਹਿਤ ਹੇਠਾਂ ਦਿੱਤੇ ਕੰਮ ਅਨੁਮਤ ਹਨ:</p>
        <ul>
          <li>ਪਾਣੀ ਸੰਰੱਖਣ ਅਤੇ ਪਾਣੀ ਸੰਚੇਈ, ਜਿਸ ਵਿੱਚ ਕੰਟੂਰ ਟ੍ਰੇਂਚ, ਕੰਟੂਰ ਬੰਧ, ਬੋਲਡਰ ਚੈਕ, ਗੈਬਿਅਨ ਸਟਰੱਕਚਰ, ਅੰਡਰਗ੍ਰਾਊਂਡ ਡਾਇਕ, ਮਿੱਟੀ ਦੇ ਬਾਂਧ, ਸਟਾਪ ਡੈਮ ਅਤੇ ਸਪ੍ਰਿੰਗਸ਼ੈਡ ਵਿਕਾਸ ਸ਼ਾਮਲ ਹਨ।</li>
          <li>ਸੁੱਖਾ ਰੋਕਥਾਮ, ਜਿਸ ਵਿੱਚ ਵਨੀਕਰਨ ਅਤੇ ਰੁੱਖ ਲਗਾਉਣ ਸ਼ਾਮਲ ਹਨ।</li>
          <li>ਸਿੰਚਾਈ ਨਹਿਰਾਂ, ਜਿਸ ਵਿੱਚ ਮਾਈਕ੍ਰੋ ਅਤੇ ਮਾਈਨਰ ਇਰੀਗੇਸ਼ਨ ਕੰਮ ਸ਼ਾਮਲ ਹਨ।</li>
          <li>ਨਿਰਧਾਰਤ ਪਰਿਵਾਰਾਂ ਦੀ ਮਲਕੀਅਤ ਵਾਲੀ ਜ਼ਮੀਨ 'ਤੇ ਸਿੰਚਾਈ ਸੁਵਿਧਾ, ਖੇਤ ਵਿੱਚ ਖੋਦਿਆ ਹੋਇਆ ਟਾਲਾਬ, ਹੋਰਟੀਕਲਚਰ, ਰੁੱਖ ਲਗਾਉਣਾ, ਖੇਤ ਮੇੜਬੰਦੀ ਅਤੇ ਜ਼ਮੀਨ ਵਿਕਾਸ।</li>
          <li>ਰਵਾਇਤੀ ਜਲ-ਸਰੋਤਾਂ ਦੀ ਮਰਮਤ, ਜਿਸ ਵਿੱਚ ਟੈਂਕਾਂ ਦੀ ਗਾਦ-ਮੁਕਤੀ ਸ਼ਾਮਲ ਹੈ।</li>
          <li>ਜ਼ਮੀਨ ਵਿਕਾਸ।</li>
          <li>ਬਾੜ ਕੰਟਰੋਲ ਅਤੇ ਸੁਰੱਖਿਆ ਕੰਮ, ਜਿਸ ਵਿੱਚ ਜਲਭਰਾਅ ਵਾਲੇ ਖੇਤਰਾਂ ਵਿੱਚ ਨਿਕਾਸੀ, ਬਾੜ ਚੈਨਲਾਂ ਦੀ ਡੂੰਘਾਈ ਅਤੇ ਮੁਰੰਮਤ, ਚੌਰ ਦੀ ਮਰਮਤ, ਅਤੇ ਤਟ-ਸੁਰੱਖਿਆ ਲਈ ਸਟਾਰਮ ਵਾਟਰ ਡਰੇਨ ਦਾ ਨਿਰਮਾਣ ਸ਼ਾਮਲ ਹੈ।</li>
          <li>ਪਿੰਡ ਸੰਪਰਕ ਲਈ ਹਰ ਮੌਸਮ ਪਹੁੰਚ ਯਕੀਨੀ ਬਣਾਉਣਾ; ਜਿੱਥੇ ਲੋੜ ਹੋਵੇ ਪੁਲੀਆ ਅਤੇ ਪਿੰਡ ਦੇ ਅੰਦਰ ਸੜਕਾਂ ਦਾ ਨਿਰਮਾਣ।</li>
          <li>ਬਲਾਕ ਪੱਧਰ 'ਤੇ ਗਿਆਨ ਸਰੋਤ ਕੇਂਦਰ ਵਜੋਂ ਭਾਰਤ ਨਿਰਮਾਣ ਰਾਜੀਵ ਗਾਂਧੀ ਸੇਵਾ ਕੇਂਦਰ ਦਾ ਨਿਰਮਾਣ ਅਤੇ ਗ੍ਰਾਮ ਪੰਚਾਇਤ ਪੱਧਰ 'ਤੇ ਗ੍ਰਾਮ ਪੰਚਾਇਤ ਭਵਨ ਦਾ ਨਿਰਮਾਣ।</li>
          <li>ਖੇਤੀਬਾੜੀ ਸੰਬੰਧੀ ਕੰਮ, ਜਿਵੇਂ NADEP ਕੰਪੋਸਟਿੰਗ, ਵਰਮੀਕੰਪੋਸਟਿੰਗ, ਤਰਲ ਜੈਵ-ਖਾਦ।</li>
          <li>ਪਸ਼ੂ-ਪਾਲਣ ਸੰਬੰਧੀ ਕੰਮ, ਜਿਵੇਂ ਪੋਲਟਰੀ ਸ਼ੈਲਟਰ, ਬੱਕਰੀ ਸ਼ੈਲਟਰ, ਪੱਕੇ ਫਰਸ਼ ਦਾ ਨਿਰਮਾਣ, ਮੂਤਰ ਟੈਂਕ ਅਤੇ ਪਸ਼ੂਆਂ ਲਈ ਚਾਰੇ ਦੀ ਨਾਲੀ, ਅਜ਼ੋਲਾ ਨੂੰ ਮਵਸ਼ੀ-ਆਹਾਰ ਪੂਰਕ ਵਜੋਂ।</li>
          <li>ਮੱਛੀ-ਪਾਲਣ ਸੰਬੰਧੀ ਕੰਮ, ਜਿਵੇਂ ਸਰਕਾਰੀ ਜ਼ਮੀਨ 'ਤੇ ਮੌਸਮੀ ਜਲਾਸਿਆਂ ਵਿੱਚ ਮੱਛੀ-ਪਾਲਣ।</li>
        </ul>
      </section>
      <footer class="mt-8 text-[15px] text-gray-500" aria-label="Attribution">ਸਰੋਤ: ਪਿੰਡ ਵਿਕਾਸ ਵਿਭਾਗ, ਹਰਿਆਣਾ।</footer>
    `

    const contentHtmlHi = `
      <section>
        <h2>प्रारंभिक</h2>
        <p>
          भारत सरकार ने सितंबर 2005 में महात्मा गांधी राष्ट्रीय ग्रामीण रोजगार गारंटी अधिनियम, 2005 (MGNREGA) पारित किया। यह अधिनियम ग्रामीण परिवारों के वयस्क सदस्यों को, जो रोजगार की मांग करें और अकुशल शारीरिक कार्य करने के इच्छुक हों, एक वित्तीय वर्ष में सौ दिनों के वेतनयुक्त रोजगार की कानूनी गारंटी देता है। अधिनियम उन क्षेत्रों पर लागू है जिन्हें केंद्र सरकार द्वारा अधिसूचित किया गया है। अधिनियम का उद्देश्य उस क्षेत्र के आधारभूत ढाँचे के विकासकारी कार्यों के माध्यम से वेतनयुक्त रोजगार पैदा कर ग्रामीण क्षेत्रों के लोगों की आजीविका सुरक्षा को बढ़ाना है।
        </p>
      </section>
      <section>
        <h2>योजना संक्षेप</h2>
        <p>
          महात्मा गांधी राष्ट्रीय ग्रामीण रोजगार गारंटी अधिनियम, 2005 के अनुसार प्रत्येक राज्य सरकार एक योजना अधिसूचित करेगी। इस संदर्भ में “हरियाणा ग्रामीण रोजगार गारंटी योजना” राष्ट्रीय ग्रामीण रोजगार गारंटी अधिनियम, 2005 के संचालन दिशानिर्देशों और प्रावधानों के अनुरूप तैयार की गई। उक्त योजना को राज्य सरकार द्वारा 16 मार्च, 2007 को हरियाणा सरकारी राजपत्र (अतिरिक्त) में अधिसूचित किया गया।
        </p>
      </section>
      <section>
        <h2>योजना का आरंभ</h2>
        <p>
          महात्मा गांधी राष्ट्रीय ग्रामीण रोजगार गारंटी योजना (MGNREGS) को भारत सरकार ने 2 फरवरी, 2006 को महेंद्रगढ़ एवं सिरसा जिलों की सभी ग्राम पंचायतों में प्रारंभ किया तथा 1 अप्रैल, 2007 से इसे दो अन्य जिलों—अंबाला एवं मेवात—में विस्तारित किया गया। राज्य के शेष जिलों को 1.4.2008 से योजना के अंतर्गत शामिल किया गया है।
        </p>
      </section>
      <section>
        <h2>उद्देश्य</h2>
        <p>
          राज्य के ग्रामीण क्षेत्रों के परिवारों की आजीविका सुरक्षा बढ़ाना, उन्हें वर्ष भर रोजगार प्रदान करना और प्रत्येक परिवार को एक वित्तीय वर्ष में कम-से-कम सौ दिनों का वेतनयुक्त रोजगार उपलब्ध कराना—बशर्ते कि वे अकुशल शारीरिक कार्य करने के इच्छुक हों। द्वितीयक उद्देश्य ग्रामीण क्षेत्रों के विकास हेतु परिसंपत्तियों का सृजन करना है।
        </p>
      </section>
      <section>
        <h2>वित्तीय भागीदारी</h2>
        <p>
          वित्तीय सहायता केंद्र और राज्य सरकारों द्वारा क्रमशः 90:10 के अनुपात में प्रदान की जाएगी।
        </p>
      </section>
      <section>
        <h2>लक्षित समूह</h2>
        <ul>
          <li>योजना उन सभी ग्रामीण परिवारों के लिए खुली है जो वेतनयुक्त रोजगार की आवश्यकता रखते हैं और अकुशल/शारीरिक कार्य करने की इच्छा रखते हैं।</li>
          <li>रोजगार की अवधि सामान्यतः कम-से-कम चौदह दिनों की सतत अवधि होनी चाहिए, एक सप्ताह में छह दिनों से अधिक नहीं।</li>
          <li>उन कार्यों को प्राथमिकता जहाँ कम-से-कम एक-तिहाई मजदूर महिलाएँ हों।</li>
          <li>कार्य के इच्छुक व्यक्ति ग्राम पंचायत में अपने आवेदन प्रस्तुत कर सकते हैं।</li>
          <li>ग्राम पंचायत परिवार का पंजीकरण करेगी। सत्यापन के बाद, आवेदक परिवार को जॉब कार्ड जारी किया जाएगा।</li>
          <li>ग्राम पंचायत या खण्ड कार्यक्रम अधिकारी, आवेदन प्राप्त होने के पंद्रह दिनों के भीतर, आवेदक को अकुशल शारीरिक कार्य प्रदान करेंगे; वरीयता ग्राम से 5 किलोमीटर के दायरे में। यदि रोजगार इस दायरे के बाहर दिया जाता है, तो यह उसी ब्लॉक के भीतर होगा और श्रमिकों को अतिरिक्त परिवहन एवं रहने के खर्च हेतु वेतन दर का 10% अतिरिक्त भुगतान किया जाएगा।</li>
        </ul>
      </section>
      <section>
        <h2>कार्यक्रम रणनीति</h2>
        <p>
          कार्यक्रम का क्रियान्वयन पंचायती राज संस्थाओं के माध्यम से किया जाएगा। ब्लॉक कार्यक्रम अधिकारी लागत के आधार पर पचास प्रतिशत कार्य ग्राम पंचायत को आवंटित करेंगे। ग्राम सभा कार्यों की अनुशंसा करेगी और ग्राम पंचायत क्रियान्वयन हेतु कार्यों की पहचान/अनुमोदन करेगी।
        </p>
        <p>
          ब्लॉक कार्यक्रम अधिकारी वार्षिक योजना की जाँच करेंगे। पंचायत समिति ग्राम पंचायत द्वारा दर्शित कार्यों की प्राथमिकता बनाए रखेगी और ब्लॉक-स्तरीय योजना को अनुमोदित करेगी।
        </p>
        <p>
          मुख्य कार्यकारी अधिकारी को ज़िले में अधिनियम/योजना के क्रियान्वयन के लिए जिला कार्यक्रम समन्वयक नामित किया गया है। DPC पंचायत समितियों की योजना प्रस्तावों की जाँच करेगा। संपूर्ण प्रक्रिया प्रत्येक वर्ष दिसंबर के अंत तक DPC द्वारा पूर्ण की जाएगी।
        </p>
      </section>
      <section>
        <h2>अनुमेय कार्य</h2>
        <p>MGNREGS के अंतर्गत निम्नलिखित कार्य अनुमेय हैं:</p>
        <ul>
          <li>जल संरक्षण और जल संचयन, जिसमें कंटूर ट्रेंच, कंटूर बण्ड, बोल्डर चेक, गैबियन संरचनाएँ, भूमिगत डाइक, मिट्टी के बाँध, स्टॉप डैम और स्प्रिंगशेड विकास शामिल हैं;</li>
          <li>सूखा-रोधी उपाय, जिसमें वनीकरण और वृक्षारोपण शामिल हैं;</li>
          <li>सिंचाई नहरें, जिनमें सूक्ष्म एवं लघु सिंचाई कार्य शामिल हैं;</li>
          <li>निर्दिष्ट परिवारों के स्वामित्व वाली भूमि पर सिंचाई सुविधा, खेत तालाब खुदाई, बागवानी, वृक्षारोपण, खेत मेड़बंदी और भूमि विकास;</li>
          <li>परंपरागत जलाशयों का पुनरोद्धार, जिसमें टैंकों का गाद निकालना शामिल है;</li>
          <li>भूमि विकास;</li>
          <li>बाढ़ नियंत्रण और संरक्षण कार्य, जिसमें जलभराव क्षेत्रों में जल निकासी, बाढ़ चैनलों की गहराई बढ़ाना और मरम्मत, चौर का पुनरुद्धार तथा तटीय संरक्षण हेतु तूफानी जल निकासी नालियों का निर्माण शामिल है;</li>
          <li>ग्रामीण संपर्क हेतु सभी मौसम में पहुँच सुनिश्चित करना; आवश्यकतानुसार पुलिया और गाँव के भीतर सड़कों का निर्माण;</li>
          <li>ब्लॉक स्तर पर ज्ञान संसाधन केन्द्र के रूप में भारत निर्माण राजीव गांधी सेवा केन्द्र का निर्माण तथा ग्राम पंचायत स्तर पर ग्राम पंचायत भवन का निर्माण;</li>
          <li>कृषि संबंधी कार्य, जैसे NADEP कम्पोस्टिंग, वर्मी कम्पोस्टिंग, तरल जैव-खाद;</li>
          <li>पशुधन संबंधी कार्य, जैसे पोल्ट्री शेल्टर, बकरी शेल्टर, पक्का फ़र्श निर्माण, मूत्र टैंक और पशुओं हेतु चारा नांद, तथा ऐज़ोला का पशु-आहार पूरक के रूप में उपयोग;</li>
          <li>मत्स्य-पालन संबंधी कार्य, जैसे सार्वजनिक भूमि पर मौसमी जलाशयों में मत्स्य-पालन;</li>
        </ul>
      </section>
      <footer class="mt-8 text-[15px] text-gray-500" aria-label="Attribution">स्रोत: ग्रामीण विकास विभाग, हरियाणा।</footer>
    `

    const contentHtml = lang === "hi" ? contentHtmlHi : lang === "pa" ? contentHtmlPa : contentHtmlEn

    const safe = sanitizeHtml(contentHtml)
    if (lang === "hi" || lang === "pa") {
      // Pre-translated high-fidelity Hindi/Punjabi content; no further translation required
      setHtml(safe)
    } else {
      setHtml(safe)
    }
  }, [lang])

  return (
    <main role="main" className="px-6 py-8">
      <article className="max-w-5xl mx-auto bg-white shadow rounded-lg border border-green-200 p-6" lang={lang} aria-label={lang === "hi" ? "MGNREGS योजना विवरण" : lang === "pa" ? "MGNREGS ਯੋਜਨਾ ਵੇਰਵਾ" : "MGNREGS scheme details"}>
        <header className="mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-green-900">
            {lang === "hi"
              ? "महात्मा गांधी राष्ट्रीय ग्रामीण रोजगार गारंटी योजना (MGNREGS)"
              : lang === "pa"
                ? "ਮਹਾਤਮਾ ਗਾਂਧੀ ਰਾਸ਼ਟਰੀ ਪਿੰਡ ਰੋਜ਼ਗਾਰ ਗਾਰੰਟੀ ਯੋਜਨਾ (MGNREGS)"
                : "MAHATMA GANDHI NATIONAL RURAL EMPLOYMENT GUARANTEE SCHEME (MGNREGS)"}
          </h1>
          <p className="text-[15px] text-gray-600">{lang === "hi" ? "ग्रामीण विकास विभाग | हरियाणा" : lang === "pa" ? "ਪਿੰਡ ਵਿਕਾਸ ਵਿਭਾਗ | ਹਰਿਆਣਾ" : "Rural Development Department | Haryana"}</p>
        </header>

        <div className="flex items-center gap-2 mb-6" aria-label={lang === "hi" ? "प्रशासनिक क्रियाएँ" : lang === "pa" ? "ਪ੍ਰਸ਼ਾਸਕੀ ਕਾਰਵਾਈਆਂ" : "Admin actions"}>
          <Link to={`/schemes/mgnregs/create?lang=${lang}`} className="px-3 py-1 rounded-md bg-blue-100 text-blue-800 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-600">{t("create_action", lang)}</Link>
          <Link to={`/schemes/mgnregs/manage?lang=${lang}`} className="px-3 py-1 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-600">{t("manage_action", lang)}</Link>
        </div>

        <div
          className={`prose max-w-none transition-opacity duration-200 ${isSwitching ? "opacity-60" : "opacity-100"} prose-h2:text-green-900 prose-h2:font-bold prose-h3:text-black prose-h3:font-extrabold prose-h4:text-black prose-h4:font-extrabold`}
          data-auto-translate="true"
          data-translated={lang !== "en" ? "true" : "false"}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </article>
    </main>
  )
}

// Shared cache moved to src/lib/schemes.ts

const SchemeDetail: React.FC = () => {
  const { slug } = useParams()
  if (slug === "mgnregs") {
    return <MGNREGSPage />
  }
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const { lang, isSwitching } = useLanguage()
  const contentLang: Language = "en"

  // Language changes handled by provider

  useEffect(() => {
    // Build localized SEO metadata
    const prefix = "Scheme"
    const localizedTitle = `${prefix}: ${slug}`
    document.title = `${localizedTitle} | Rural Development Department | Haryana`
    setMetaTag("description", `Details for scheme ${slug} in Haryana: objectives, eligibility, procedures`)
    setMetaTag("og:title", localizedTitle)
    setMetaTag("og:type", "article")
    setLinkTag("canonical", "https://haryanarural.gov.in/schemes/")
  }, [slug])

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    setHtml(null)
    const cached = slug ? getCachedScheme(slug, contentLang) : null
    if (cached && alive) {
      setHtml(cached)
      setLoading(false)
      return () => { alive = false }
    }
    ; (async () => {
      try {
        const content = slug ? await prefetchScheme(slug, contentLang) : null
        if (!alive) return
        if (content) {
          setHtml(content)
        } else {
          throw new Error("Content loading failed")
        }
      } catch (e: any) {
        if (alive) {
          const msg = e?.message || "Content loading failed"
          setError(msg)
          // Log for diagnostics without disrupting UX
          console.error("[SchemeDetail] Content fetch error:", msg, { slug, lang })
        }
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [slug])

  return (
    <main role="main" className="px-6 py-8">
      {loading && (
        <div className="max-w-5xl mx-auto bg-white shadow rounded-lg border border-green-200 p-6" aria-busy="true" aria-label="Loading scheme">
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin h-6 w-6 border-4 border-green-600 border-t-transparent rounded-full" aria-hidden="true" />
            <span className="ml-3 text-gray-700">Loading scheme content…</span>
          </div>
        </div>
      )}
      {!loading && (
        <article className={`max-w-5xl mx-auto bg-white shadow rounded-lg border border-green-200 p-6 ${isSwitching ? "opacity-95" : "opacity-100"}`} aria-label="Scheme details" lang={contentLang}>
          <header className="mb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-green-900">Scheme Details</h1>
            <p className="text-[15px] text-gray-600">Rural Development Department | Haryana</p>
          </header>

          <div className="flex items-center gap-2 mb-6" aria-label="Admin actions">
            <Link to={`/schemes/${slug}/create?lang=${lang}`} className="px-3 py-1 rounded-md bg-blue-100 text-blue-800 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-600">{t("create_action", "en")}</Link>
            <Link to={`/schemes/${slug}/manage?lang=${lang}`} className="px-3 py-1 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-600">{t("manage_action", "en")}</Link>
          </div>

          {error && (
            <div role="alert" className="p-3 rounded-md bg-red-50 text-red-700 border border-red-200">
              {error}. Please refresh the page or try again later.
            </div>
          )}
          {!error && !html && (
            <div role="note" className="p-3 rounded-md bg-yellow-50 text-yellow-800 border border-yellow-200">
              {lang === "hi"
                ? "इस योजना के लिए सामग्री अभी उपलब्ध नहीं है। कृपया बाद में पुनः जाँचें या मुखपृष्ठ पर जाएँ।"
                : lang === "pa"
                  ? "ਇਸ ਯੋਜਨਾ ਲਈ ਸਮੱਗਰੀ ਇਸ ਵੇਲੇ ਉਪਲਬਧ ਨਹੀਂ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਬਾਅਦ ਵਿੱਚ ਦੁਬਾਰਾ ਜਾਂਚ ਕਰੋ ਜਾਂ ਮੁੱਖ ਸਫ਼ੇ 'ਤੇ ਜਾਓ।"
                  : "Content for this scheme is not yet available. Please check back later or visit the homepage."}
              <div className="mt-2">
                <Link
                  to={lang === "en" ? "/" : `/${lang}`}
                  className="underline text-green-700 hover:text-green-800 active:text-green-900"
                >
                  {lang === "hi" ? "मुखपृष्ठ" : lang === "pa" ? "ਮੁੱਖ ਸਫ਼ਾ" : "Homepage"}
                </Link>
              </div>
            </div>
          )}
          {!error && html && (
            <div
              className={`prose max-w-none transition-opacity duration-200 ${isSwitching ? "opacity-60" : "opacity-100"} prose-h2:text-green-900 prose-h2:font-bold prose-h3:text-black prose-h3:font-extrabold prose-h4:text-black prose-h4:font-extrabold`}
              aria-label="Scheme content"
              data-auto-translate="true"
              data-translated={lang !== "en" ? "true" : "false"}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
        </article>
      )}
    </main>
  )
}

export default SchemeDetail
